import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from '@carbon/react';
import axios from 'axios';
import moment from 'moment';

import { GatewayIp, endpoints } from '../../../data/endpoints';

export interface RecordNowResult {
  slotId: string;
  slotLabel: string;
  scheduleId?: string;
  status: 'success' | 'error';
  message?: string;
}

interface Props {
  open: boolean;
  results: RecordNowResult[];
  startAt: string;
  endAt: string;
  token: string | null;
  onClose: () => void;
  onChanged: () => void;
}

const authHeaders = (token: string) => ({
  Authorization: `Bearer ${token}`,
  'Content-Type': 'application/json',
});

const dtInputStyle: React.CSSProperties = {
  width: '100%',
  backgroundColor: '#262626',
  border: '1px solid #525252',
  color: '#fff',
  padding: '9px 12px',
  borderRadius: 4,
  fontSize: 14,
  boxSizing: 'border-box',
  colorScheme: 'dark',
};

const toInputValue = (value: string) => (value ? moment(value).format('YYYY-MM-DDTHH:mm') : '');

const RecordNowModal: React.FC<Props> = ({
  open,
  results,
  startAt,
  endAt,
  token,
  onClose,
  onChanged,
}) => {
  const [editableEndAt, setEditableEndAt] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }

    setEditableEndAt(toInputValue(endAt));
    setSubmitting(false);
    setError(null);
  }, [endAt, open]);

  const successfulResults = useMemo(
    () => results.filter((result) => result.status === 'success' && result.scheduleId),
    [results]
  );

  const startMoment = useMemo(() => moment(startAt), [startAt]);
  const endMoment = useMemo(() => moment(editableEndAt), [editableEndAt]);
  const isEndValid = Boolean(editableEndAt) && startMoment.isValid() && endMoment.isValid() && endMoment.isAfter(startMoment);
  const hasChanges = toInputValue(endAt) !== editableEndAt;

  const handleSubmit = async () => {
    if (!token || !successfulResults.length || !isEndValid) {
      return;
    }

    if (!hasChanges) {
      onClose();
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await Promise.all(
        successfulResults.map((result) =>
          axios.patch(
            `${GatewayIp + endpoints.scheduler.schedules}/${result.scheduleId}`,
            { endAt: endMoment.utc().toISOString() },
            { withCredentials: true, headers: authHeaders(token) }
          )
        )
      );

      onChanged();
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message ?? 'Failed to update record end time.');
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      size="sm"
      modalHeading="Record Now"
      primaryButtonText={hasChanges ? 'Update End Time' : 'Update'}
      secondaryButtonText="Close"
      primaryButtonDisabled={submitting || successfulResults.length === 0 || !isEndValid}
      onRequestClose={onClose}
      onRequestSubmit={handleSubmit}
      onSecondarySubmit={onClose}
      preventCloseOnClickOutside
    >
      <p style={{ color: '#8d8d8d', fontSize: 13, marginBottom: 16 }}>
        Created record{successfulResults.length !== 1 ? 's' : ''} for <strong style={{ color: '#ccc' }}>{results.length}</strong> slot{results.length !== 1 ? 's' : ''}.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div>
          <label style={{ display: 'block', fontSize: 12, marginBottom: 6, color: '#ccc' }}>Start At</label>
          <input type="datetime-local" value={toInputValue(startAt)} readOnly style={{ ...dtInputStyle, opacity: 0.7 }} />
          <div style={{ fontSize: 11, color: '#8d8d8d', marginTop: 4 }}>Timezone: {Intl.DateTimeFormat().resolvedOptions().timeZone}</div>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 12, marginBottom: 6, color: '#ccc' }}>End At</label>
          <input
            type="datetime-local"
            value={editableEndAt}
            onChange={(e) => setEditableEndAt(e.target.value)}
            style={dtInputStyle}
          />
          {!isEndValid ? (
            <div style={{ fontSize: 11, color: '#fa4d56', marginTop: 4 }}>End time must be after start time.</div>
          ) : (
            <div style={{ fontSize: 11, color: '#8d8d8d', marginTop: 4 }}>Timezone: {Intl.DateTimeFormat().resolvedOptions().timeZone}</div>
          )}
        </div>

        {error ? <div style={{ fontSize: 12, color: '#fa4d56' }}>{error}</div> : null}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
          {results.map((result) => (
            <div
              key={`${result.slotId}-${result.scheduleId ?? result.status}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '8px 12px',
                borderRadius: 4,
                backgroundColor: '#1e1e1e',
                border: `1px solid ${result.status === 'success' ? '#24a148' : '#da1e28'}`,
              }}
            >
              <span style={{ fontSize: 16 }}>{result.status === 'success' ? '✓' : '✕'}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, color: '#ccc', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {result.slotLabel}
                </div>
                {result.status === 'error' && result.message ? (
                  <div style={{ fontSize: 11, color: '#fa4d56', marginTop: 2 }}>{result.message}</div>
                ) : null}
              </div>
              <span
                style={{
                  fontSize: 11,
                  color: result.status === 'success' ? '#24a148' : '#da1e28',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                }}
              >
                {result.status}
              </span>
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
};

export default RecordNowModal;
